import { useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../ui/button";
import { authApi } from "../../lib/authApi";

const BASE = { customer: (id) => `/orders/${id}`, partner: (id) => `/partner/orders/${id}`, admin: (id) => `/admin/orders/${id}` };

export const OrderDocuments = ({ order, role }) => {
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState(null);
  const [busy, setBusy] = useState(null);
  const base = BASE[role](order.id);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      try {
        const r = await authApi.get(`${base}/documents`);
        setDocs(r.data);
      } catch {
        setDocs([]);
        toast.error("Could not load documents.");
      }
    }
  };

  const download = async (d) => {
    setBusy(d.type);
    try {
      const res = await authApi.get(`${base}/documents/${d.type}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${d.ref.replace(/\//g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Document download failed.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-4" data-testid={`order-documents-${order.id}`}>
      <button onClick={toggle} className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline" data-testid={`order-documents-toggle-${order.id}`}>
        <FileText className="h-3.5 w-3.5" strokeWidth={1.5} />Documents{open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <div className="mt-2 p-3 rounded-lg bg-muted/40" data-testid={`order-documents-list-${order.id}`}>
          {!docs && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {docs && (
            <div className="flex flex-wrap gap-2">
              {docs.map((d) => (
                <Button key={d.type} size="sm" variant="outline" className="rounded-full h-8" disabled={!d.available || busy === d.type} title={d.available ? d.ref : d.reason}
                  onClick={() => download(d)} data-testid={`order-doc-${d.type}-btn-${order.id}`}>
                  {busy === d.type ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileText className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />}
                  {d.label}{d.ref ? <span className="ml-1.5 font-mono-tech text-[10px] text-muted-foreground">{d.ref}</span> : null}
                </Button>
              ))}
            </div>
          )}
          {docs?.some((d) => !d.available) && (
            <p className="mt-2 text-xs text-muted-foreground" data-testid={`order-documents-unavailable-${order.id}`}>
              {docs.filter((d) => !d.available).map((d) => `${d.label}: ${d.reason}`).join(" · ")}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
